import React, {useEffect, useState} from 'react';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import axios from 'axios';
import * as yup from 'yup';
import {useFormik} from 'formik';
import {useStoreActions} from 'easy-peasy';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import {useAlert} from '@/components/alerts';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';  
import {buildURL} from '@/utils/urlBuilder';
import AsyncSelect from 'react-select/async';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface Agent {
    id: number;
    name: string;
    externalId: string;
}

interface AgentTeamSettings {
    agentTeamId?: number;
    agentId?: number;
    teamId?: number;
    activeBetween: {
        start: string;
        end: string;
    };
}

interface AssignAgentSettingsFormProps {
    agentTeamId?: number;
    agentId?: number;
    teamId?: number;
    onSave: () => void;
}

interface AgentOption {
    label: string;
    value: number;
}

interface AgentsResponse {
    data: {
        items: Agent[];
    };
}

const AgentTeamSettingsFormSchema = yup.object({
    agentId: yup.number().positive().integer().required(i18n.t('jt_st_agent_team_agent_id.text')),
    teamId: yup.number().positive().integer().required(i18n.t('jt_st_agent_team_team_id.text')),
    activeBetween: yup.object({
        start: yup.date()
            .required(i18n.t('jt_st_agent_team_active_between_start.text'))
            .max(yup.ref('end'), i18n.t('jt_st_agent_team_active_between_end.text')),
        end: yup.date()
            .nullable()
            .min(yup.ref('start'), i18n.t('jt_st_agent_team_active_between_end.text')),
    }).required(i18n.t('jt_agent_first_name_required.text')),
});

const AssignAgentSettingsForm: React.FC<AssignAgentSettingsFormProps> = React.memo((
    {
        agentTeamId = 0,
        agentId = 0,
        teamId = 0,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const agentRestricted = restrictedFieldKeys.includes('agent_pii');
    let agentDetailUrl = '';

    if (agentId && teamId && agentTeamId) {
        agentDetailUrl = buildURL('assignAgentToTeamResourceDetail', {
            agent_id: agentId,
            team_id: teamId,
            agent_team_id: agentTeamId
        });
    }

    const [selectedAgent, loadingAgentData] = useData<AgentTeamSettings>(agentDetailUrl, {useLocalStore: true});

    const setAgent = useStoreActions((actions: any) => actions.agents.addOrEdit);

    const loading = agentId && loadingAgentData;

    const onSubmit = async (values: AgentTeamSettings) => {
        let response;
        let requestMethod: (url: string, payload: any) => Promise<any>;
        let url = window.URLS.assignAgentToTeamResource;
        let actionName: string;

        const payload = {
            team_id: parseInt(values.teamId?.toString() || '0', 10),
            agent_id: parseInt(values.agentId?.toString() || '0', 10),
            active_between: `${values.activeBetween.start},${values.activeBetween.end}`
        };

        if (agentId) {
            url = `${url}/${agentTeamId}`;
            requestMethod = axios.put;
            actionName = 'changed';
        } else {
            requestMethod = axios.post;
            actionName = 'created';
        }

        try {
            response = await requestMethod(url, payload);
        } catch (e: any) {
            if (e.response?.status === 400) {
                formik.setFieldError('activeBetween.start', e.response?.data?.message ?? i18n.t('invalid_data.text'));
                return;
            }
            throw e;
        }
        onSave();

        setAgent(response.data);
        showSuccess(i18n.t('jt_st_agent_team_success_action.text', {actionName}));
    };

    const formik = useFormik<AgentTeamSettings>({
        initialValues: {
            agentTeamId: agentTeamId,
            agentId: agentId,
            teamId: teamId,
            activeBetween: {
                start: selectedAgent?.activeBetween?.start || '',
                end: selectedAgent?.activeBetween?.end || ''
            }
        },
        validationSchema: AgentTeamSettingsFormSchema,
        enableReinitialize: true,
        onSubmit,
    });

    const loadAgentsRequest = (inputValue: string) => axios.get<AgentsResponse>(window.URLS.agentsResource, {
        params: {
            query: inputValue,
        },
    }).catch((error: any) => {
        if (error.response?.status === 400) {
            return error.response;
        }

        throw error;
    });

    const loadAgentsRequestDebounced = AwesomeDebouncePromise(loadAgentsRequest, 280);

    const getAgentOptions = async (inputValue: string): Promise<AgentOption[]> => {
        const results = await loadAgentsRequestDebounced(inputValue);
        return results.data.items.map((agent: Agent) => ({
            label: `${agent.name || ''} (${agent.externalId || ''})`.trim() || `Agent #${agent.id}`,
            value: agent.id,
        }));
    };

    const [agentName, setAgentName] = useState<string>('');
    const [agentExternalId, setAgentExternalId] = useState<string>('');

    useEffect(() => {
        const fetchData = async () => {
            if (!formik.values.agentId) {return;}

            const response = await axios.get<Agent>(buildURL('agentDetails', {agent_id: formik.values.agentId}));
            const agent = response.data;

            setAgentName(agent.name);
            setAgentExternalId(agent.externalId);
        };

        if (formik.values.agentId && !agentName) {
            fetchData();
        }
    }, [formik.values.agentId, agentName]);

    if (loading) {
        return (<CellLoader />);
    }

    return (
        <>
            <Form noValidate onSubmit={formik.handleSubmit}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="agentId" data-cy="select-agent" >
                            <Form.Label>{i18n.t('jt_agent.text')}</Form.Label>
                            {agentRestricted ? (
                                <RestrictedFieldInput />
                            ) : (
                                <AsyncSelect<AgentOption>
                                    value={{
                                        label: `${agentName} (${agentExternalId})`,
                                        value: formik.values.agentId || 0,
                                    }}
                                    loadOptions={getAgentOptions}
                                    isDisabled={true}
                                    cacheOptions
                                    defaultOptions
                                    hideSelectedOptions={false}
                                    onBlur={formik.handleBlur}
                                    aria-label="select-agent"
                                />
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="startDate" >
                            <Form.Label>{i18n.t('jt_started_team.text')}</Form.Label>
                            <Form.Control
                                type="date"
                                name="activeBetween.start"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.activeBetween.start || ''}
                                isInvalid={formik.touched.activeBetween?.start && !!formik.errors.activeBetween?.start}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.activeBetween?.start}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row >

                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="endDate" >
                            <Form.Label>{i18n.t('jt_left_the.text')}</Form.Label>
                            <Form.Control
                                type="date"
                                name="activeBetween.end"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.activeBetween.end || ''}
                                isInvalid={formik.touched.activeBetween?.end && !!formik.errors.activeBetween?.end}
                            />
                            <Form.Control.Feedback type="invalid">
                                {formik.errors.activeBetween?.end}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row >

                <Row>
                    <Form.Group className="ml-auto mb-3" controlId="story-save">
                        <Button
                            variant="info"
                            fullWidth
                            type="submit"
                            data-cy="assign-agent-settings-info-btn">
                            {i18n.t('jt_save.text')}
                        </Button>
                    </Form.Group>
                </Row>
            </Form >
        </>
    );
});


export default AssignAgentSettingsForm; 